// TODO: COMBINE SEPARATORS AND SHELVES SELECTORS!

import { useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import classNames from 'classnames';
import styles from './ShelvesSelector.module.scss';
import { WidenImage } from '@common/Widen/WidenImage';
import { NumberInput } from '@common/NumberInput/NumberInput';
import { CHAINFLEX_MAX_BUY_LENGTH, CHAINFLEX_MIN_BUY_QUANTITY, getLocale } from '@libs/constants';
import { Button, TYPE } from '@common/Button/Button';
import { Link } from '@common/Link/Link';
import { Dropdown } from '@common/Dropdown/Dropdown';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { Badge, BadgeColor } from '@common/Badge/Badge';
import { AssociationType } from '@libs/associations';
import type {
  ModelAssociationProductFragmentEnhanced,
  ModelAssociationFragmentEnhanced,
} from '@models/enhanced-fragments';
import type { ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue, getAttributeValueAsString } from '@libs/attribute.helper';
import { NotifyPopup } from '@product/VariantSelector/NotifyPopup/NotifyPopup';
import { getProductIdForLocale } from '@libs/product.helper';
import { getUnitLabelForUnitSystem } from '@icommerce/units';
import type { ShelveItem, SortedProductWidthValues } from './utils/useShelveItems';
import { SHELVE_ITEM_MAX_DISPLAY, useShelveItems } from './utils/useShelveItems';

export interface ShelvesSelectorProps {
  shelvesAssociationModels: ModelAssociationFragmentEnhanced[] | null;
  onItemAdd?: (item: ShelveItem) => void;
}

const getOptionMap = (sortedProductWidthValues: SortedProductWidthValues[]) =>
  new Map<string, string>(sortedProductWidthValues.map(({ label, id }) => [label, id]));

export const ShelvesSelector = ({ shelvesAssociationModels, onItemAdd }: ShelvesSelectorProps) => {
  const router = useRouter();
  const locale = getLocale(router);
  const unitSystem = useProductPageStore(state => state.unitSystem);
  const { t } = useTranslation(['product/echainBuyBox', 'layout/general', 'product/buyBox']);
  const [showMoreShelves, setShowMoreShelves] = useState(false);
  // map each model with the selected product
  const [selectedAssociationProductPerModel, setSelectedAssociationProductPerModel] = useState<
    Record<string, ModelAssociationProductFragmentEnhanced>
  >({});
  const listLength = shelvesAssociationModels?.length || 0;
  const setSelectedInteriorSeparator = useEchainProductStore(state => state.setSelectedInteriorSeparator);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const addSelectedAssociationModelProduct = (id: string, product: ModelAssociationProductFragmentEnhanced) => {
    const records = { ...selectedAssociationProductPerModel };
    records[id] = product;
    setSelectedAssociationProductPerModel({ ...records });
  };
  const standardShelveModelKey = getAttributeValue<string>(
    currentProduct.attributes?.standard_shelf as ProductAttribute<string>
  );
  const [itemCount, setItemCount] = useState<Map<number, number>>(new Map<number, number>());
  const [showPopup, setShowPopup] = useState<Map<number, boolean>>(new Map<number, boolean>());

  const handleAddItemClick = async (shelveItem: ShelveItem) => {
    if (!currentProduct || !itemCount.get(shelveItem.key) || !shelveItem.selectedAssociationProduct) {
      return;
    }

    await setSelectedInteriorSeparator(
      currentProduct,
      shelveItem.selectedAssociationProduct,
      AssociationType.SEPARATORS,
      locale,
      (shelveItem.partsListItem?.quantity ?? 0) + (itemCount.get(shelveItem.key) ?? 1)
    );

    setItemCount(current => {
      current.delete(shelveItem.key);
      return new Map<number, number>(current);
    });

    setShowPopup(current => {
      current.forEach((v, k) => v && current.set(k, false));
      return new Map<number, boolean>(current.set(shelveItem.key, true));
    });

    if (onItemAdd) {
      onItemAdd(shelveItem);
    }
  };

  const shelveItems = useShelveItems(
    shelvesAssociationModels,
    selectedAssociationProductPerModel,
    showMoreShelves,
    standardShelveModelKey
  );

  return (
    <div className="grid grid-cols-1 gap-y-4 divide-y divide-gray-200">
      <div>
        {shelveItems && shelveItems.length > 0 && (
          <>
            <div
              className={classNames(
                'grid ',
                'grid-cols-1',
                `sm:grid-cols-2`,
                `md:grid-cols-3`,
                `lg:grid-cols-4`,
                'gap-8',
                'mb-4 p-2',
                'place-content-center'
              )}
            >
              {shelveItems.map(shelveItem => (
                <NotifyPopup
                  key={`${AssociationType.SHELVES}-element-${shelveItem.key}`}
                  type="success"
                  show={showPopup.get(shelveItem.key) ?? false}
                  onHide={() => setShowPopup(current => new Map<number, boolean>(current.set(shelveItem.key, false)))}
                  content={t('ECHAIN_PRODUCT_PAGE.TOOLTIPS.ADDED_TO_PARTSLIST')}
                  containerProps={{
                    'data-testid': `${AssociationType.SHELVES}-element-${shelveItem.key}`,
                    className: 'px-6 pb-3 mt-6 relative border border-gray-200 bg-white flex flex-col',
                  }}
                >
                  <>
                    {shelveItem.associatedModel.products?.[0].model_id === standardShelveModelKey && (
                      <div className={classNames('-ml-6')}>
                        <Badge text="Standard" BackgroundColor={BadgeColor.Primary} />
                      </div>
                    )}
                    {shelveItem.partsListItem && (
                      <div className="top-2 right-4 absolute">
                        <Icon id={IconID.CHECK} color={IconColor.ORANGE} size={12} primary={false} />
                      </div>
                    )}
                    <div
                      className={classNames(
                        'm-2',
                        standardShelveModelKey !== undefined && 'pt-14',
                        'text-center',
                        'font-semibold',
                        'text-sm',
                        'grow',
                        'mb-0'
                      )}
                    >
                      {shelveItem.associatedModel.products?.[0].attributes?.series_description
                        ? getAttributeValueAsString(
                            shelveItem.associatedModel.products[0].attributes
                              .series_description as ProductAttribute<string>
                          )
                        : ''}
                    </div>
                    {shelveItem.associatedModel.products ? (
                      <div className="text-center h-8 mb-0 text-gray-400">
                        <small data-testid="product-id-display-name" className="text-gray-300">
                          {getProductIdForLocale(
                            selectedAssociationProductPerModel[shelveItem.associatedModel.id] ??
                              shelveItem.associatedModel.products.find(
                                p => p.id === shelveItem.sortedProductWidthValues[0].id
                              ),
                            locale
                          )}
                        </small>
                      </div>
                    ) : null}
                    <div className={classNames(styles.interiorSeparationImgWrapper, 'h-0 p-1 mt-8', 'relative')}>
                      <WidenImage
                        filename={shelveItem.teaserImage?.filename}
                        shortCode={shelveItem.teaserImage?.shortCode}
                        title={shelveItem.teaserImage?.title}
                        width={0}
                        height={0}
                        layout="fill"
                        objectFit="contain"
                      />
                    </div>

                    <div className="text-center text-sm h-10 mb-0 mt-8 text-gray-400">
                      {shelveItem.sortedProductWidthValues[0].width} -{' '}
                      {shelveItem.sortedProductWidthValues[shelveItem.sortedProductWidthValues.length - 1].width} [
                      {getUnitLabelForUnitSystem(shelveItem.sortedProductWidthValues[0].unitCode, unitSystem)}]
                    </div>

                    <div className="mt-4 flex flex-col">
                      <Dropdown
                        id={`dropdown-${AssociationType.SHELVES}-${shelveItem.key}`}
                        className="flex-1 mt-3 mb-3"
                        values={getOptionMap(shelveItem.sortedProductWidthValues)}
                        testidSuffix={`-${AssociationType.SHELVES}-${shelveItem.key}`}
                        // set the new selected AssociationProduct
                        onChange={value => {
                          const selectedProduct = shelveItem.associatedModel.products.find(
                            product => product.id === value
                          );
                          selectedProduct &&
                            addSelectedAssociationModelProduct(shelveItem.associatedModel.id, selectedProduct);
                        }}
                      />
                      <NumberInput
                        onChange={value =>
                          setItemCount(current => {
                            if (value === undefined) {
                              current.delete(shelveItem.key);
                              return new Map<number, number>(current);
                            }

                            return new Map<number, number>(current.set(shelveItem.key, value));
                          })
                        }
                        className="flex-1 mt-2"
                        value={itemCount.get(shelveItem.key)}
                        min={1}
                        placeholder={t('GENERAL.PLACEHOLDER.QUANTITY', { ns: 'layout/general' })}
                        max={CHAINFLEX_MAX_BUY_LENGTH}
                        errorMessage={t('BUY_BOX.UNITS_INPUT_ERRORMESSAGE', {
                          min: CHAINFLEX_MIN_BUY_QUANTITY,
                          max: CHAINFLEX_MAX_BUY_LENGTH,
                          ns: 'product/buyBox',
                        })}
                        onlyInt
                      />
                      <Button
                        disabled={!itemCount.get(shelveItem.key)}
                        icon={
                          <Icon
                            size={4}
                            color={!itemCount.get(shelveItem.key) ? IconColor.GRAY : IconColor.ORANGE}
                            id={IconID.PLUS}
                          />
                        }
                        text={t('GENERAL.ADD', { ns: 'layout/general' })}
                        type={TYPE.TERTIARY}
                        minW={false}
                        onClick={async () => {
                          await handleAddItemClick(shelveItem);
                        }}
                      />
                    </div>
                  </>
                </NotifyPopup>
              ))}
            </div>
            <div className="px-3">
              {!showMoreShelves && listLength > SHELVE_ITEM_MAX_DISPLAY && (
                <Link
                  className="uppercase text-sm"
                  onClick={() => {
                    setShowMoreShelves(true);
                  }}
                  primary
                  text={t('ECHAIN_PRODUCT_PAGE.INTERIOR_SEPARATORS.MORE_VERTICAL_ELEMENTS')}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
